"use client";

import { useEffect, useState } from "react";
import { StatTile } from "@/components/homepage/stat-tile";
import {
  seededCaffeineSeries,
  seriesTrend,
  type CaffeineSeriesOptions,
  type WeeklySeries,
} from "@/lib/homepage-telemetry";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";

/** seededCaffeineSeries returns ng/mL; the tile displays µg/mL. */
const NG_PER_UG = 1000;
const UPDATE_INTERVAL_MS = 15_000;

interface CaffeineTileProps {
  seed: number;
  days: number;
  samplesPerDay: number;
  opts: CaffeineSeriesOptions;
  /** Server-computed series for the initial render, so hydration matches exactly. */
  initialSeries: WeeklySeries;
}

function toMicrogramsPerMl(series: WeeklySeries): WeeklySeries {
  return series.map((point) => ({ ...point, value: point.value / NG_PER_UG }));
}

/**
 * Client-side counterpart to the other (server-only, revalidate-hour) stat tiles:
 * recomputes the pharmacokinetic model against the real current time on an interval,
 * so the headline number and sparkline visibly decay/rise without a page refresh.
 */
export function CaffeineTile({ seed, days, samplesPerDay, opts, initialSeries }: CaffeineTileProps) {
  const { shouldAnimate } = useReducedMotion();
  const [series, setSeries] = useState(initialSeries);

  useEffect(() => {
    if (!shouldAnimate) return;

    function refresh() {
      setSeries(seededCaffeineSeries(seed, days, samplesPerDay, opts, new Date().toISOString()));
    }

    const intervalId = window.setInterval(refresh, UPDATE_INTERVAL_MS);

    function handleVisibilityChange() {
      if (!document.hidden) refresh();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [seed, days, samplesPerDay, opts, shouldAnimate]);

  const displaySeries = toMicrogramsPerMl(series);
  const value = displaySeries[displaySeries.length - 1].value;

  return (
    <StatTile
      label="caffeine"
      value={value.toFixed(2)}
      unit="µg/mL"
      series={displaySeries}
      trend={seriesTrend(displaySeries)}
      estimated
    />
  );
}
